'use client'

/**
 * Migration Prompt Banner
 *
 * Displays a dismissible banner prompting users to migrate from Supabase to Automerge.
 * Shows only to users who haven't migrated and haven't dismissed the prompt.
 *
 * Features:
 * - Prominent call-to-action for migration
 * - Highlights benefits: offline-first, faster, privacy
 * - Dismissible (saves to localStorage)
 * - Links to migration page
 */

import { useState } from 'react'
import { Alert, AlertDescription } from '@/ui/alert'
import { Button } from '@/ui/button'
import {
  XIcon,
  DownloadSimpleIcon,
  LightningIcon,
  ShieldCheckIcon,
} from '@phosphor-icons/react'
import Link from 'next/link'
import { useMigrationStatus } from '../discovery/use-migration-status'

export function MigrationPromptBanner() {
  const { hasMigrated, isDismissed, dismissPrompt, loading } =
    useMigrationStatus()
  const [isVisible, setIsVisible] = useState(true)

  // Don't show if migrated, dismissed, or loading
  if (loading || hasMigrated || isDismissed || !isVisible) {
    return null
  }

  const handleDismiss = () => {
    setIsVisible(false)
    dismissPrompt()
  }

  return (
    <Alert className="border-info/30 bg-info/5 relative">
      <DownloadSimpleIcon className="text-info h-4 w-4" />
      <AlertDescription className="ml-2 flex items-start justify-between gap-4">
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <p className="text-foreground font-semibold">
              Upgrade to Local-First Storage
            </p>
          </div>

          <p className="text-muted-foreground text-sm">
            Migrate your data to work fully offline with faster performance and
            complete data ownership. Your data stays on your device and syncs
            across all your devices.
          </p>

          <div className="text-muted-foreground flex flex-wrap gap-4 text-xs">
            <div className="flex items-center gap-1">
              <LightningIcon className="h-3 w-3" />
              <span>Instant access</span>
            </div>
            <div className="flex items-center gap-1">
              <DownloadSimpleIcon className="h-3 w-3" />
              <span>Works offline</span>
            </div>
            <div className="flex items-center gap-1">
              <ShieldCheckIcon className="h-3 w-3" />
              <span>Your data, your device</span>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button asChild size="sm" className="h-8">
              <Link href="/app/migrate">Migrate Now</Link>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-info hover:bg-info/10 hover:text-info h-8"
              asChild
            >
              <Link href="/app/migrate">View Migration Details</Link>
            </Button>
          </div>
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:bg-muted hover:text-foreground h-6 w-6 shrink-0"
          onClick={handleDismiss}
        >
          <XIcon className="h-4 w-4" />
          <span className="sr-only">Dismiss</span>
        </Button>
      </AlertDescription>
    </Alert>
  )
}
